import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Warning,
  TrendingDown,
  Schedule,
  Cancel,
  ExpandMore,
  CheckCircle,
  PriorityHigh,
} from '@mui/icons-material';

interface ImprovementRecommendationsProps {
  courseId: string;
}

const ImprovementRecommendations: React.FC<ImprovementRecommendationsProps> = ({ courseId }) => {
  // Mock data
  const lowPassRateTopics = [
    {
      topicId: 1,
      topicName: 'Trigonometry',
      passRate: 68,
      threshold: 70,
      recommendation: 'Review content difficulty and add more practice exercises',
      priority: 'high' as const,
    },
    {
      topicId: 2,
      topicName: 'Calculus Basics',
      passRate: 65,
      threshold: 70,
      recommendation: 'Break down into smaller subtopics and add prerequisite review',
      priority: 'high' as const,
    },
  ];

  const highAttemptTopics = [
    {
      topicId: 2,
      topicName: 'Calculus Basics',
      avgAttempts: 3.5,
      threshold: 3,
      recommendation: 'Simplify content or adjust difficulty level',
      priority: 'high' as const,
    },
    {
      topicId: 3,
      topicName: 'Polynomials',
      avgAttempts: 2.8,
      threshold: 3,
      recommendation: 'Add more examples and step-by-step solutions',
      priority: 'medium' as const,
    },
  ];

  const longCompletionTopics = [
    {
      topicId: 2,
      topicName: 'Calculus Basics',
      estimatedTime: 80,
      actualTime: 95,
      difference: 15,
      recommendation: 'Content may be too complex, consider splitting or simplifying',
      priority: 'high' as const,
    },
    {
      topicId: 1,
      topicName: 'Trigonometry',
      estimatedTime: 70,
      actualTime: 85,
      difference: 15,
      recommendation: 'Review time estimates or provide additional support materials',
      priority: 'medium' as const,
    },
  ];

  const highAbandonmentTopics = [
    {
      topicId: 2,
      topicName: 'Calculus Basics',
      abandonmentRate: 15,
      recommendation: 'Improve engagement with interactive content and clearer explanations',
      priority: 'high' as const,
    },
    {
      topicId: 1,
      topicName: 'Trigonometry',
      abandonmentRate: 12,
      recommendation: 'Add motivational elements and break into smaller chunks',
      priority: 'medium' as const,
    },
  ];

  const overallRecommendations = [
    {
      category: 'Content Quality',
      priority: 'high' as const,
      description: 'Several topics show low pass rates indicating content may need improvement',
      suggestedActions: [
        'Review and update Trigonometry content',
        'Add more practice exercises for Calculus Basics',
        'Include more real-world examples',
      ],
    },
    {
      category: 'Difficulty Adjustment',
      priority: 'high' as const,
      description: 'Some topics are too difficult, causing high attempt counts',
      suggestedActions: [
        'Simplify Calculus Basics content',
        'Add prerequisite knowledge checks',
        'Create intermediate difficulty versions',
      ],
    },
    {
      category: 'Time Estimation',
      priority: 'medium' as const,
      description: 'Time estimates need adjustment for better planning',
      suggestedActions: [
        'Update time estimates based on actual data',
        'Add time tracking for better estimates',
      ],
    },
  ];

  const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Improvement Recommendations
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        AI-driven insights to identify areas needing improvement in your course
      </Typography>

      <Grid container spacing={3}>
        {/* Overall Recommendations */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Overall Recommendations
            </Typography>
            {overallRecommendations.map((rec, index) => (
              <Accordion key={index} sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Chip
                      label={rec.priority.toUpperCase()}
                      color={getPriorityColor(rec.priority)}
                      size="small"
                    />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {rec.category}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {rec.description}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                    Suggested Actions:
                  </Typography>
                  <List dense>
                    {rec.suggestedActions.map((action, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckCircle fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={action} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>

        {/* Low Pass Rate Topics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Warning color="error" />
              <Typography variant="h6">
                Low Pass Rate Topics
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Pass Rate</TableCell>
                    <TableCell align="right">Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowPassRateTopics.map((topic) => (
                    <TableRow key={topic.topicId}>
                      <TableCell>{topic.topicName}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${topic.passRate}%`} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={topic.priority.toUpperCase()}
                          color={getPriorityColor(topic.priority)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" sx={{ mt: 2 }} onClick={() => {/* Navigate to details */}}>
              View All Low Pass Rate Topics
            </Button>
          </Paper>
        </Grid>

        {/* High Attempt Topics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TrendingDown color="warning" />
              <Typography variant="h6">
                High Attempt Topics
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Avg Attempts</TableCell>
                    <TableCell align="right">Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {highAttemptTopics.map((topic) => (
                    <TableRow key={topic.topicId}>
                      <TableCell>{topic.topicName}</TableCell>
                      <TableCell align="right">
                        <Chip label={topic.avgAttempts} color="warning" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={topic.priority.toUpperCase()}
                          color={getPriorityColor(topic.priority)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" sx={{ mt: 2 }} onClick={() => {/* Navigate to details */}}>
              View All High Attempt Topics
            </Button>
          </Paper>
        </Grid>

        {/* Long Completion Topics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Schedule color="warning" />
              <Typography variant="h6">
                Long Completion Time Topics
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Time Diff</TableCell>
                    <TableCell align="right">Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {longCompletionTopics.map((topic) => (
                    <TableRow key={topic.topicId}>
                      <TableCell>{topic.topicName}</TableCell>
                      <TableCell align="right">
                        <Chip label={`+${topic.difference} min`} color="warning" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={topic.priority.toUpperCase()}
                          color={getPriorityColor(topic.priority)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" sx={{ mt: 2 }} onClick={() => {/* Navigate to details */}}>
              View All Long Completion Topics
            </Button>
          </Paper>
        </Grid>

        {/* High Abandonment Topics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Cancel color="error" />
              <Typography variant="h6">
                High Abandonment Topics
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Abandonment</TableCell>
                    <TableCell align="right">Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {highAbandonmentTopics.map((topic) => (
                    <TableRow key={topic.topicId}>
                      <TableCell>{topic.topicName}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${topic.abandonmentRate}%`} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={topic.priority.toUpperCase()}
                          color={getPriorityColor(topic.priority)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" sx={{ mt: 2 }} onClick={() => {/* Navigate to details */}}>
              View All High Abandonment Topics
            </Button>
          </Paper>
        </Grid>

        {/* Priority Ranking */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <PriorityHigh color="error" />
              <Typography variant="h6">
                Improvement Priority Ranking
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Topic</TableCell>
                    <TableCell>Issue</TableCell>
                    <TableCell align="right">Priority Score</TableCell>
                    <TableCell align="right">Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>1</TableCell>
                    <TableCell>Calculus Basics</TableCell>
                    <TableCell>Low pass rate, high attempts, long completion</TableCell>
                    <TableCell align="right">9.5</TableCell>
                    <TableCell align="right">
                      <Chip label="HIGH" color="error" size="small" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>2</TableCell>
                    <TableCell>Trigonometry</TableCell>
                    <TableCell>Low pass rate, long completion, high abandonment</TableCell>
                    <TableCell align="right">8.2</TableCell>
                    <TableCell align="right">
                      <Chip label="HIGH" color="error" size="small" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3</TableCell>
                    <TableCell>Polynomials</TableCell>
                    <TableCell>High attempts</TableCell>
                    <TableCell align="right">6.5</TableCell>
                    <TableCell align="right">
                      <Chip label="MEDIUM" color="warning" size="small" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ImprovementRecommendations;

